import {Alert, Box} from "@mui/material";
import React from "react";
import {Occupation, OccupationPersonDay} from "arteco-api-client-ts";


export interface CalendarProps {
	occupation: Occupation;
	personId: number;
	month: string;
}

/** Por debajo de esta diferencia se considera que planificado e imputado cuadran. */
const HOURS_EPSILON = 0.01;

/** Evita que la resta de dos float saque un 0.30000000000000004 en pantalla. */
const round2 = (num: number): number => Math.round(num * 100) / 100;

const plannedOf = (row: OccupationPersonDay): number => row.availableHours || 0;
const imputedOf = (row: OccupationPersonDay): number => row.imputedHours || 0;

/** Sólo se marca el descuadre; el signo de la diferencia ya dice de qué lado cojea. */
const isMismatch = (row: OccupationPersonDay): boolean =>
	Math.abs(imputedOf(row) - plannedOf(row)) >= HOURS_EPSILON;

const MISMATCH_BG = "rgba(255, 167, 38, 0.18)";
const MISMATCH_FG = "#a15c00";

const signed = (num: number): string => (num > 0 ? "+" : "") + round2(num);

export const Occupation_calendar = ({occupation, month, personId}: CalendarProps) => {

	const calendar = occupation.calendarByMonthAndPersonId?.[month]?.[personId] || [];

	// Sin este dato, un mes que todavía no se ha importado y otro en el que nadie ha imputado
	// se verían igual: una columna de ceros que parece un error.
	const hasImputedData = (occupation.monthsWithImputedData || []).includes(Number(month));

	const totalPlanned = round2(calendar.reduce((acc, row) => acc + plannedOf(row), 0));
	const totalImputed = round2(calendar.reduce((acc, row) => acc + imputedOf(row), 0));
	const totalDiff = round2(totalImputed - totalPlanned);

	// El cuadre bueno del mes es contra las dedicaciones, que es lo que queda tras la fase de
	// distribución. Aquí sólo se puede desglosar por día lo imputado en Redmine, porque una
	// dedicación es de un mes entero y no tiene fecha.
	const summary = occupation.planningByMonthAndPersonId?.[month]?.[personId];
	const totalDedicated = round2(summary?.workedHours || 0);
	const dedicatedDiff = round2(totalDedicated - totalPlanned);
	const dedicatedMismatch = Math.abs(dedicatedDiff) >= HOURS_EPSILON;

	// Quien no ha imputado nada en todo el mes sencillamente no usa Redmine: marcar sus treinta
	// días en ámbar es ruido, sobre todo cuando sus dedicaciones cuadran. El desglose diario sólo
	// dice algo de quien sí imputa.
	const personImputes = hasImputedData && totalImputed > 0;

	const cellStyle: React.CSSProperties = {paddingRight: "2em"};

	return (
		<>
			<Alert severity={dedicatedMismatch ? "warning" : "success"} sx={{mb: 2}}>
				Planificado <b>{totalPlanned} h</b> y dedicado <b>{totalDedicated} h</b>
				{dedicatedMismatch ? ` (${signed(dedicatedDiff)} h sin cuadrar).` : " — cuadra."}
				{" "}El detalle por día de abajo es lo imputado en Redmine, que es la materia prima
				antes de distribuir: puede no coincidir sin que eso sea un problema.
			</Alert>

			{!hasImputedData && (
				<Alert severity="info" sx={{mb: 2}}>
					No hay horas importadas de Redmine para este mes, así que la columna IMPUTADO
					va vacía. Se cargan desde <b>Importar redmine</b>.
				</Alert>
			)}

			<table className={"table-calendar"} style={{width: "100%"}}>
				<thead>
				<tr>
					<th align={"left"}>FECHA</th>
					<th align={"left"}>DÍA</th>
					<th align={"left"}>TRABAJO</th>
					<th align={"left"}>IMPUTADO</th>
					<th align={"left"}>DIF</th>
				</tr>
				</thead>
				<tbody>
				{calendar.map((row, rowIdx) => {
					const imputed = imputedOf(row);
					const diff = round2(imputed - plannedOf(row));
					const mismatch = personImputes && isMismatch(row);

					return (
						<tr key={rowIdx}
							style={{
								color: row.weekend || row.holiday ? "red" : "",
								backgroundColor: mismatch ? MISMATCH_BG : ""
							}}>
							<td style={{paddingRight: "1em"}}>
								{row.date}
							</td>
							<td style={cellStyle}>
								{row.weekday}
							</td>
							<td style={cellStyle}>
								{row.holiday && (
									<Box color={"red"}>
										{row.holiday}
									</Box>
								)}
								{!row.holiday && (
									<>
										{!row.availableHours && row.weekend && !row.vacation && (
											<span>---</span>
										)}

										{!row.vacation && !row.weekend && (
											<>{row.availableHours}</>
										)}
										{row.vacation && (
											<Box color={
												row.vacationState ==
												"ACCEPTED"
													? "primary.main"
													: "text.disabled"
											}>
												{row.vacationType as string}
											</Box>
										)}

									</>
								)}

							</td>
							<td style={cellStyle}>
								{!personImputes && <span style={{color: "#999"}}>—</span>}
								{personImputes && imputed === 0 && (
									<span style={{color: mismatch ? MISMATCH_FG : "#999"}}>
										{mismatch ? "0" : "---"}
									</span>
								)}
								{personImputes && imputed > 0 && (
									<span style={{
										color: mismatch ? MISMATCH_FG : "",
										fontWeight: mismatch ? 600 : "inherit"
									}}>
										{round2(imputed)}
									</span>
								)}
							</td>
							<td style={{color: MISMATCH_FG, fontWeight: 600}}>
								{mismatch && signed(diff)}
							</td>
						</tr>
					);
				})}
				</tbody>
				<tfoot>
				<tr style={{borderTop: "2px solid #ccc", fontWeight: 600}}>
					<td colSpan={2} style={{paddingTop: "0.5em"}}>TOTAL</td>
					<td style={{...cellStyle, paddingTop: "0.5em"}}>{totalPlanned}</td>
					<td style={{...cellStyle, paddingTop: "0.5em"}}>
						{personImputes ? totalImputed : <span style={{color: "#999"}}>—</span>}
					</td>
					<td style={{
						paddingTop: "0.5em",
						color: personImputes && Math.abs(totalDiff) >= HOURS_EPSILON ? MISMATCH_FG : ""
					}}>
						{personImputes && Math.abs(totalDiff) >= HOURS_EPSILON && signed(totalDiff)}
					</td>
				</tr>
				</tfoot>
			</table>
		</>
	)
}
